import {StringHelper} from '~/helpers/stringHelper'
import {IValidator, IValidatorOptions, IValidatorOptionsSimple} from '~/interfaces/validatorInterfaces'
import {BooleanHelper} from "~/helpers/booleanHelper"
import { ArrayHelper } from '~/helpers/arrayHelper'

/**
 * The interface for every Validator.
 * Holds all the core logic for interfacing with other classes
 *   +validate+, +isValid+,
 *
 * R: return type of the +evaluator+ property - e.g. the type of the conditional rule result
 * Simple: is "basic" version that represent the fundamental data of the properties
 *   @example if enabled can be a boolean or a function that returns a boolean, then boolean is the "simple" version
 */
export default abstract class Validator<R, Simple extends IValidatorOptionsSimple<R> = IValidatorOptionsSimple<R>, O extends IValidatorOptions<R> = any> implements IValidator {
  /**
   * Result of running +validate+
   * Should be set during the subclasses +_validate+ execution
   */
  isValid: boolean = null
  /**
   * Should be set during the subclasses +_validate+ execution
   */
  errorMessage: string = ''
  /**
   * Has this validator been registered with a ValidatorGroup? (or whatever else we may hook it up to)
   * Used to prevent double registering a validator
   */
  registered: boolean = false
  /**
   * Return value of the +evaluator+ function
   */
  result: R = null
  /**
   * Provide a template for error messages. Useful when only small parts of the error message are dynamic
   */
  abstract messageTemplate: string

  constructor(protected options: IValidatorOptions<R>) {
  }

  /**
   * Unique identifier of this Validator.
   * Can be anything. I've been using it as a description.
   * @example 'PresenceValidator', 'CharacterCountValidator', 'SignatureValidator'
   */
  get id(): string {
    return this.call("id")
  }

  /**
   * The custom validation message if provided
   */
  get validationMessage(): string {
    return this.call("validationMessage")
  }

  /**
   * Should this Validator even run?
   */
  get enabled(): boolean {
    return this.call("enabled")
  }

  /**
   * An override for this validator to use as +_validate+ args instead of
   * args passed into +validate+ method
   */
  get value(): any {
    return this.call("value")
  }

  /**
   * Run the validation. Store errorMessage and isValid as a result.
   * The validator will not be run unless +enabled+ evaluates to true (not truthy)
   * @param args
   */
  validate(...args: Array<unknown>): void {
    this.resetValidate()
    this.result = this.call('evaluator')
    if (BooleanHelper.isStrictTrue(this.enabled)) {
      this._validate(...ArrayHelper.wrap(this.value ?? args))
    } else {
      this.isValid = true
    }
  }

  /**
   * The core logic of validating should be implemented here, in subclasses
   * Set +isValid+ and +errorMessage+ in this method
   * @protected
   */
  protected abstract _validate(...args: Array<any>): void

  /**
   * Generic function for executing a function if the value of the property is a function
   * or otherwise returning the value of the property
   *
   * @param prop
   * @protected
   */
  protected call<K extends keyof Simple>(prop: K): Simple[K] {
    const value = (<any>this.options)[prop]
    return isFunction<Simple[K]>(value) ? value(this.result) : value
  }

  /**
   * Interpolate the hash of data provide into the message template to construct the final message.
   *
   * @param interpolations data to be inserted into the message template
   * @protected
   */
  protected buildMessage(interpolations: Record<string, string> = {}): string {
    return StringHelper.interpolate(this.messageTemplate, interpolations)
  }

  /**
   * Reset this Validator as if the +validate+ was never called.
   * This is called right before +_validate+ is run in the +validate+ function to ensure
   * results from separate `validate` called do not get mixed together.
   * @private
   */
  private resetValidate(): void {
    this.isValid = false
    this.errorMessage = ''
    this.result = null
  }
}

/**
 * Basic user type guard function to check if something is a function
 * @param value
 */
function isFunction<V>(value: unknown): value is (...args) => V {
  return typeof value === 'function'
}
